import { Customer } from '@squareup/sq-addons-web-sdk'
import { formatPhoneNumber } from 'utils/phoneNumber'
import { User } from 'api/types'
import { order as squareOrderApi } from '@squareup/sq-addons-web-sdk'

export enum CustomerDisplayNameType {
  NAME,
  EMAIL,
  PHONE_NUMBER,
}

const DEFAULT_CUSTOMER_DISPLAY_NAME_PRECEDENCE_LIST = [
  CustomerDisplayNameType.NAME,
  CustomerDisplayNameType.EMAIL,
  CustomerDisplayNameType.PHONE_NUMBER,
]

/**
 * Builds the customer display name from the customer object and customer
 * display name precedence list. By default, it will try to return the customer
 * name first, then email address, and then phone number. If none of the
 * information is present, returns `Unknown name`.
 *
 * @param customer The customer to get the display name for.
 * @param precedenceList The precedence list for the customer display name.
 * @returns The customer display name.
 */
export function getCustomerDisplayName(
  customer: Customer,
  precedenceList = DEFAULT_CUSTOMER_DISPLAY_NAME_PRECEDENCE_LIST
) {
  for (const customerDisplayNameType of precedenceList) {
    switch (customerDisplayNameType) {
      case CustomerDisplayNameType.NAME: {
        if (customer?.familyName && customer?.givenName) {
          return `${customer.givenName} ${customer.familyName}`
        } else if (customer?.givenName) {
          return customer.givenName
        } else if (customer?.familyName) {
          return customer.familyName
        }
        break
      }

      case CustomerDisplayNameType.EMAIL: {
        if (customer?.emailAddress) {
          return customer.emailAddress
        }
        break
      }

      case CustomerDisplayNameType.PHONE_NUMBER: {
        if (customer?.phoneNumber) {
          return formatPhoneNumber(customer.phoneNumber)
        }
        break
      }

      default:
        break
    }
  }

  return 'Unknown name'
}

export function convertUserToCustomer(user: User): Customer {
  return {
    referenceId: user.identifier,
    givenName: user.first_name,
    familyName: user.last_name,
    emailAddress: user.email,
    phoneNumber: user.phone,
  }
}

export async function getOrderCustomer(): Promise<Customer | null> {
  try {
    return await squareOrderApi.getCustomer()
  } catch (err) {
    return null
  }
}
